import '.q{cursor:pointer;}.ssr-plugin'
import { styled } from 'inlines'
import { Avatar } from '../Avatar/index.js'
import { Menu } from '../Menu/index.js'
import { Tooltip } from '../Tooltip/index.js'
import { useUsers } from '../../hooks/useUsers.js'
import { hash } from '@saulx/hash'
import { useFollowMe } from '../../hooks/useFollowMe.js'
import { useEffect, useState } from 'react'
import { capitalize } from '../../utils/text.js'
import { useTheme } from '../../hooks/useTheme.js'
import { Color, colors } from '../../tokens/colors.js'
import { useClient } from '@based/sdk/react'
import { useLocation } from '../../hooks/useCmsParams.js'
import { useUser } from '../../hooks/useUser.js'

export const userAvatarColors: Color[] = Object.entries(colors)
  .filter(([key]) => {
    if (key.includes('chart') && !key.includes('Mono')) {
      return true
    }
    return false
  })
  .map(([key]) => key) as Color[]

export const UserAvatar = ({
  disabled = false,
  move = false,
  email,
  name,
  src,
  id,
  index,
  color,
  zIndex = 0,
  onClick,
}: {
  disabled?: boolean
  move?: boolean
  email: string
  name: string
  src: string
  id: number
  index: number
  color: Color
  zIndex?: number
  onClick?: () => void
}) => {
  const { me } = useUsers()

  return (
    <Tooltip description={name} key={`${id || Date.now()}-${index}-${email}`}>
      <styled.div className="q"
        style={{
          marginRight: !move ? 0 : -8,
          zIndex,
        }}
        onClick={onClick}
      >
        <Avatar
          src={src}
          spotlight={me?.followMe?.following?.id === id}
          disabled={disabled}
          label={name}
          color={color}
        />
      </styled.div>
    </Tooltip>
  )
}

type UserMenuProps = {
  logoutEndpoint?: string
  hideAccountSettings?: boolean
  hideWorkspaceSettings?: boolean
  followMe?: boolean
}

export const UserMenu = ({
  logoutEndpoint = 'ui-auth-logout',
  hideAccountSettings,
  hideWorkspaceSettings,
  followMe: isFollowMeEnabled,
}: UserMenuProps) => {
  let [, navigate] = useLocation()
  const me = useUser()
  const myHash = hash(me?.email + me?.name)
  const [followMe, setFollowMe] = useFollowMe()
  const [privateMode, setPrivateMode] = useState<boolean>(false)
  const { theme, setTheme } = useTheme()
  const client = useClient()

  useEffect(() => {
    if (followMe.privateMode !== privateMode) {
      setPrivateMode(followMe.privateMode)
    }
  }, [followMe.privateMode, privateMode])

  useEffect(() => {
    setFollowMe({ hash: myHash })
  }, [myHash])

  return (
    <Menu>
      <Menu.Trigger>
        <UserAvatar
          email={me?.email}
          src={me?.picture}
          name={me?.name || me?.email}
          id={me?.id}
          index={me?.id}
          color={userAvatarColors[myHash % userAvatarColors.length]}
        />
      </Menu.Trigger>
      <Menu.Items>
        {me?.name && me?.email && (
          <>
            <Menu.Item label={capitalize(me?.name)} description={me?.email} />
            <Menu.Separator />
          </>
        )}
        {!hideAccountSettings && (
          <Menu.Item
            label="Account settings"
            leadIcon="user-setting"
            onClick={() => {
              navigate('account-settings')
            }}
          />
        )}
        {!hideWorkspaceSettings && me?.role === 'admin' && (
          <Menu.Item
            label="Workspace settings"
            leadIcon="settings"
            onClick={() => {
              navigate('workspace-settings')
            }}
          />
        )}
        <Menu position="right">
          <Menu.Trigger>
            <Menu.Item
              keepOpenOnClick
              leadIcon={
                theme === 'dark'
                  ? 'dark'
                  : theme === 'light'
                    ? 'light'
                    : 'boolean'
              }
              trailIcon="chevron-right"
              label={`Theme (${capitalize(theme)})`}
              onClick={() => {}}
            />
          </Menu.Trigger>
          <Menu.Items>
            <Menu.Item
              keepOpenOnClick
              indentContent={theme !== 'light'}
              selected={theme === 'light'}
              label="Light"
              onClick={() => setTheme('light')}
            />
            <Menu.Item
              keepOpenOnClick
              indentContent={theme !== 'dark'}
              selected={theme === 'dark'}
              label="Dark"
              onClick={() => setTheme('dark')}
            />
            <Menu.Separator />
            <Menu.Item
              keepOpenOnClick
              indentContent={theme !== 'auto'}
              selected={theme === 'auto'}
              label="Auto"
              onClick={() => setTheme('auto')}
            />
          </Menu.Items>
        </Menu>
        {isFollowMeEnabled && (
          <Menu position="right">
            <Menu.Trigger>
              <Menu.Item
                keepOpenOnClick
                leadIcon="show"
                trailIcon="chevron-right"
                label={`Private Mode (${privateMode ? 'On' : 'Off'})`}
                onClick={() => {}}
              />
            </Menu.Trigger>
            <Menu.Items>
              <Menu.Item
                keepOpenOnClick
                indentContent={!privateMode}
                selected={privateMode}
                label="On"
                onClick={() => setFollowMe({ privateMode: true })}
              />
              <Menu.Item
                keepOpenOnClick
                indentContent={privateMode}
                selected={!privateMode}
                label="Off"
                onClick={() => setFollowMe({ privateMode: false })}
              />
            </Menu.Items>
          </Menu>
        )}
        <Menu.Separator />
        <Menu.Item
          label="Logout"
          leadIcon="log-out"
          color="red"
          onClick={async () => {
            setFollowMe({ following: { id: null, color: null }, followers: [] })

            try {
              await client.call(logoutEndpoint)
            } catch {}
            await client.setAuthState({})
          }}
        />
      </Menu.Items>
    </Menu>
  )
}
